import axios from "axios";

/** ===== Configuração da API Principal (Azure) ===== */
const rawBaseURL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://api-arenastats-d5bnakdcdbb8b5gw.brazilsouth-01.azurewebsites.net";

// AJUSTE CRÍTICO: Adiciona o prefixo /api à URL base para corresponder ao backend
const baseURL = `${rawBaseURL.replace(/\/+$/, "")}/api`;

const AZURE = axios.create({
  baseURL: baseURL,
  timeout: 60000, // 60s para cold starts e queries pesadas
  withCredentials: false,
});

// Exporta a instância AZURE para uso direto quando necessário
export { AZURE };

/** ===== Interceptor de Retry Exponencial ===== */
const retryInterceptor = async (error) => {
  const config = error.config || {};
  
  // Inicializa contador de retry
  if (!config._retryCount) {
    config._retryCount = 0;
  }
  
  // Retry em caso de timeout ou network error (máximo 2 tentativas)
  // Skip retry for heavy endpoints (video upload/inference) to avoid re-uploading
  const shouldRetry = 
    !config._skipRetry &&
    config._retryCount < 2 && 
    (error.code === 'ECONNABORTED' || 
     error.code === 'ERR_NETWORK' ||
     error.response?.status >= 500);
  
  if (shouldRetry) {
    config._retryCount += 1;
    const delay = 500 * (2 ** config._retryCount); // Exponencial: 1s, 2s
    
    console.log(`[ArenaStats] Retry ${config._retryCount}/2 após ${delay}ms para ${config.url}`);
    
    await new Promise(resolve => setTimeout(resolve, delay));
    return AZURE(config);
  }
  
  return Promise.reject(error);
};

/** ===== Interceptor de Log de Erros ===== */
const logErrorInterceptor = (err) => {
  // VOICE-FINAL-INTEGRATION-1: cancelamento intencional (AbortController —
  // barge-in, cancelar conexão) é controle de fluxo, não falha de API.
  // Nunca como console.error: senão não dá para distinguir erro real.
  if (axios.isCancel?.(err) || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
    if (typeof console.debug === 'function') console.debug(`[ArenaStats] request canceled: ${err.config?.url || ''}`);
    return Promise.reject(err);
  }
  const status = err.response?.status;
  const url = err.config?.url;
  const msg = err.response?.data?.detail || err.response?.data?.error || err.message;
  console.error(`[ArenaStats] API error: ${status} on ${url} -> ${msg}`);
  return retryInterceptor(err);
};

AZURE.interceptors.response.use((r) => r, logErrorInterceptor);

/** ===== Interceptor de Request para adicionar JWT Token ===== */
let tokenInterceptorInitialized = false;

/** ===== Domain Prefix Rewriter (VUKZ + ARENA logical separation) =====
 *  Rewrites legacy /api/* paths to domain-prefixed /api/vukz/* or /api/arena/*.
 *  Backend triple-registers /api/vukz/*, /api/sucron/*, and legacy /api/* prefixes.
 *  This interceptor ensures the frontend uses the canonical new paths.
 *  Remove this once legacy prefixes are decommissioned (Phase 3, May 2026).
 */
const DOMAIN_PREFIX_MAP = [
  // VUKZ domain (Performance OS) — order matters: longer prefixes first
  ['/my/',               '/vukz/my/'],
  ['/training/',         '/vukz/training/'],
  ['/workouts/',         '/vukz/workouts/'],
  ['/tactics/',          '/vukz/tactics/'],
  ['/playbook/',         '/vukz/playbook/'],
  ['/manager/',          '/vukz/manager/'],
  ['/mind-kernel/',      '/vukz/mind-kernel/'],
  ['/mind-performance/', '/vukz/mind-performance/'],
  ['/mind/',             '/vukz/mind/'],
  ['/assist/',           '/vukz/assist/'],
  ['/sports/',           '/vukz/sports/'],
  ['/research/',         '/vukz/research/'],
  ['/agenda/',           '/vukz/agenda/'],
  ['/agents/',           '/vukz/agents/'],
  ['/rbac/',             '/vukz/rbac/'],

  // ARENA domain (Data Infrastructure)
  ['/parser/',           '/arena/parser/'],
  // TEMPORARY: Disable /swimming/ → /arena/swimming/ until backend is updated
  // ['/swimming/',         '/arena/swimming/'],
  ['/football/',         '/arena/football/'],
  ['/ingestion/',        '/arena/ingestion/'],
  ['/orchestrator-tool/', '/arena/orchestrator-tool/'],
  ['/auditor/',          '/arena/auditor/'],
  ['/researcher/',       '/arena/researcher/'],
  // governance lives at /api/governance/* (no arena prefix)
  ['/library/',          '/arena/library/'],
  ['/entity-resolution/', '/arena/entity-resolution/'],
  ['/normalizer/',       '/arena/normalizer/'],
  ['/analytics/',        '/arena/analytics/'],
];

AZURE.interceptors.request.use((config) => {
  // Domain prefix rewrite: /my/* → /vukz/my/*, /swimming/* → /arena/swimming/*, etc.
  if (config.url && !config.url.includes('/vukz/') && !config.url.includes('/arena/') && !config.url.includes('/sucron/')) {
    for (const [legacy, newPrefix] of DOMAIN_PREFIX_MAP) {
      if (config.url.startsWith(legacy)) {
        config.url = config.url.replace(legacy, newPrefix);
        break;
      }
    }
  }

  // OPS Mode: Send API key instead of JWT (SWA Entra ID auth, no JWT available)
  if (process.env.REACT_APP_OPS_MODE === 'true') {
    const opsApiKey = process.env.REACT_APP_OPS_API_KEY;
    if (opsApiKey) {
      config.headers['X-Ops-Api-Key'] = opsApiKey;
    }
    // Forward SWA user email if available (set by OpsApp)
    const opsEmail = window.__OPS_USER_EMAIL__;
    if (opsEmail) {
      config.headers['X-Ops-User-Email'] = opsEmail;
    }
  }

  // Recupera token do localStorage (silenciosamente)
  const userStr = localStorage.getItem('arena_stats_user');
  
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      if (user.token) {
        config.headers.Authorization = `Bearer ${user.token}`;
        
        // Log apenas na primeira vez (debugging)
        if (!tokenInterceptorInitialized) {
          console.log('[ArenaStats] JWT token configured');
          tokenInterceptorInitialized = true;
        }
      }
    } catch (e) {
      console.error('[api.js] ❌ Erro ao parsear user do localStorage:', e);
    }
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

/** ===== Helper para remover parâmetros nulos/vazios ===== */
const withParams = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ""));

/** ======================================
 * Endpoints da API do Azure (Seu Backend)
 * ====================================== */

// As chamadas permanecem as mesmas. O Axios combinará a baseURL com estes caminhos.
// Ex: ".../api" + "/football/leagues" = ".../api/football/leagues"
export const getFootballMeta = () => AZURE.get("/football/leagues");

// Endpoint para buscar metadados das ligas (id, nome, temporadas)
export const getLeaguesMeta = () => AZURE.get("/football/leagues");

export const getLeagueStandings = ({ leagueId, season }) =>
  AZURE.get("/football/standings", { params: withParams({ leagueId, season }) });

export const getLeagueRound = ({ leagueId, season, round }) =>
  AZURE.get("/football/round", { params: withParams({ leagueId, season, round }) });

export const getMatchesByDate = ({ date, leagueId, tz }) => {
  const params = { date };
  if (leagueId) params.leagueId = leagueId;
  // Garantir que sempre envia timezone
  params.tz = tz || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo';
  
  return AZURE.get('/football/matches-by-date', { params: withParams(params) });
};

export const getRecentMatches = ({ leagueId, limit }) => AZURE.get('/football/recent-matches', { params: withParams({ leagueId, limit }) });

export const getAllStats = ({ league, season }) => 
  AZURE.get("/football/all-stats", { params: withParams({ league, season }) });

export const getLeagueComparison = ({ from, to, leagues }) =>
  AZURE.get("/football/league-comparison", { params: withParams({ from, to, leagues: Array.isArray(leagues) ? leagues.join(",") : leagues }) });

export const getLeagueSeasonStats = ({ leagueIds, season }) =>
  AZURE.get("/football/league-season-stats", { 
    params: withParams({ 
      leagueIds: Array.isArray(leagueIds) ? leagueIds.join(",") : leagueIds, 
      season 
    }) 
  });

export const getTopPlayerStats = ({ leagueId, season }) =>
  AZURE.get("/football/top-player-stats", { params: withParams({ leagueId, season }) });

export const getTopTeamStats = ({ leagueId, season }) =>
  AZURE.get("/football/top-team-stats", { params: withParams({ leagueId, season }) });

export const getTopTeamsByMetric = ({ leagueIds, season, metric, limit = 10 }) =>
  AZURE.get("/football/top-teams-by-metric", { 
    params: withParams({ 
      leagueIds: Array.isArray(leagueIds) ? leagueIds.join(",") : leagueIds, 
      season,
      metric,
      limit
    }) 
  });

export const getTopPlayersByMetric = ({ leagueIds, season, metric, limit = 10 }) =>
  AZURE.get("/football/top-players-by-metric", { 
    params: withParams({ 
      leagueIds: Array.isArray(leagueIds) ? leagueIds.join(",") : leagueIds, 
      season,
      metric,
      limit
    }) 
  });

// === NOVOS ENDPOINTS PER-GAME (Métricas por Jogo) ===
export const getTopTeamsByMetricPerGame = ({ leagueIds, season, metric, limit = 10 }) =>
  AZURE.get("/football/top-teams-by-metric-per-game", { 
    params: withParams({ 
      leagueIds: Array.isArray(leagueIds) ? leagueIds.join(",") : leagueIds, 
      season,
      metric,
      limit
    }) 
  });

export const getTopPlayersByMetricPerGame = ({ leagueIds, season, metric, limit = 10 }) =>
  AZURE.get("/football/top-players-by-metric-per-game", { 
    params: withParams({ 
      leagueIds: Array.isArray(leagueIds) ? leagueIds.join(",") : leagueIds, 
      season,
      metric,
      limit
    }) 
  });

// --- NOVOS ENDPOINTS PARA COMPARATIVO DE LIGAS (1 melhor por liga) ---

export const getLeagueAveragesPerGame = ({ leagueIds, season, metrics }) =>
  AZURE.get("/football/league-averages-per-game", {
    params: withParams({
      leagueIds: Array.isArray(leagueIds) ? leagueIds.join(",") : leagueIds,
      season,
      metrics: Array.isArray(metrics) ? metrics.join(",") : metrics
    })
  });

export const getBestTeamPerLeague = ({ leagueIds, season, metric }) =>
  AZURE.get("/football/best-team-per-league", {
    params: withParams({
      leagueIds: Array.isArray(leagueIds) ? leagueIds.join(",") : leagueIds,
      season,
      metric
    })
  });

export const getBestPlayerPerLeague = ({ leagueIds, season, metric }) =>
  AZURE.get("/football/best-player-per-league", {
    params: withParams({
      leagueIds: Array.isArray(leagueIds) ? leagueIds.join(",") : leagueIds,
      season,
      metric
    })
  });

// --- Endpoints para a MatchPage ---
export const getMatchById = ({ id }) => AZURE.get(`/football/matches/${id}`);
export const getLineups = ({ matchId }) => AZURE.get(`/football/lineups/${matchId}`);
export const getMatchStatistics = ({ matchId }) => AZURE.get(`/football/statistics/${matchId}`);
export const getMatchBoxScore = ({ matchId }) => AZURE.get(`/football/box-score/${matchId}`);
export const getLiveEvents = ({ matchId }) => AZURE.get(`/football/events/${matchId}`);
export const getHighlights = ({ matchId }) => AZURE.get(`/football/highlights/${matchId}`);

// Endpoint antigo mantido por segurança
export const getTop5 = () => {
    console.warn("A função getTop5 foi descontinuada. Use getAllStats para melhor performance.");
    return Promise.resolve({ data: { items: [] } });
}

/** ===== Função para Registro de Usuário ===== */
export async function registerUser(payload) {
  console.log('[api.js] 📤 registerUser chamado');
  console.log('[api.js] 🔍 Payload:', { ...payload, password: '***' });
  console.log('[api.js] 🌐 URL completa:', `${AZURE.defaults.baseURL}/auth/register`);
  
  try {
    const response = await AZURE.post('/auth/register', payload);
    console.log('[api.js] ✅ Resposta recebida:', response);
    console.log('[api.js] 📦 Response.data:', response.data);
    return response.data;
  } catch (error) {
    console.error('[api.js] ❌ Erro na requisição:', error);
    console.error('[api.js] 🔍 Error.response:', error.response);
    console.error('[api.js] 🔍 Error.response.data:', error.response?.data);
    throw error;
  }
}

/** ===== Função para Login de Usuário ===== */
export async function loginUser(payload) {
  console.log('[api.js] 📤 loginUser chamado');
  console.log('[api.js] 🔍 Email:', payload.email);
  console.log('[api.js] 🌐 URL completa:', `${AZURE.defaults.baseURL}/auth/login`);
  
  try {
    const response = await AZURE.post('/auth/login', payload);
    console.log('[api.js] ✅ Login bem-sucedido:', response.data);
    return response.data;
  } catch (error) {
    console.error('[api.js] ❌ Erro no login:', error);
    console.error('[api.js] 🔍 Error.response.data:', error.response?.data);
    throw error;
  }
}

/** ======================================
 * Endpoints de Venues (Polos Esportivos)
 * ====================================== */

// Listar todos os venues (com filtros opcionais)
export const getVenues = ({ city, type } = {}) =>
  AZURE.get("/venues", { params: withParams({ city, type }) });

// Buscar detalhes completos de um venue
export const getVenueDetails = (venueId) =>
  AZURE.get(`/venues/${venueId}`);

// Listar venues de uma cidade específica
export const getVenuesByCity = (city) =>
  AZURE.get(`/venues/city/${city}`);

/** ======================================
 * Endpoints de Natação (Swimming)
 * ====================================== */

// Buscar rankings por prova de todas as competições do ano (top 10 feminino e masculino)
export const getSwimmingRankings = ({ year, limit = 10, countries }) =>
  AZURE.get("/swimming/rankings", { params: withParams({ year, limit, countries }) });

// Buscar Ranking Arena 2025 (Top 10 por prova/gênero)
export const getArenaRankings = () => AZURE.get("/swimming/rankings/arena");

// Buscar ranking completo de um evento específico
export const getRankings = (eventId, year, limit = 100) => 
  AZURE.get("/swimming/rankings", { params: withParams({ event_id: eventId, year, limit }) });

console.log("[ArenaStats] API baseURL para chamadas =", AZURE.defaults.baseURL);